import React, { useState, useEffect } from "react";
import { getAvailableRoomsForTimeSlot } from "../utils/roomAllocation";

const ConflictTable = ({ conflicts, data, onResolveConflicts }) => {
  const [resolutions, setResolutions] = useState({});
  const [availableRooms, setAvailableRooms] = useState({});
  const [changesProcessed, setChangesProcessed] = useState(false);

  // Calculate available rooms for each time slot using the same logic as "Find Unallocated"
  useEffect(() => {
    const roomsByTimeSlot = {};

    conflicts.forEach((conflict) => {
      // Convert display format (e.g., "Mon Eve") back to the format used by getAvailableRoomsForTimeSlot (e.g., "mon eve")
      const timeSlot = conflict.day_time.toLowerCase();

      // Use the same function as "Find Unallocated" to ensure consistency
      const availableForTimeSlot = getAvailableRoomsForTimeSlot(data, timeSlot);

      roomsByTimeSlot[timeSlot] = availableForTimeSlot;
    });

    setAvailableRooms(roomsByTimeSlot);

    // CRITICAL: Clear resolutions when conflicts change to prevent cross-contamination
    setResolutions({});

    // Reset processed flag when conflicts change
    setChangesProcessed(false);
  }, [conflicts, data]);
  const handleRoomChange = (conflictId, rowIndex, newRoom) => {
    setResolutions((prev) => ({
      ...prev,
      [`${conflictId}_${rowIndex}`]: newRoom,
    }));
  };

  // Validation function to check for duplicate room selections within the same conflict
  const validateRoomSelection = (conflictId, rowIndex, selectedRoom) => {
    if (!selectedRoom) return { isValid: true, message: "" };

    // Find the conflict by ID to get the conflicting rows
    const conflict = conflicts.find((c) => c.conflict_id === conflictId);
    if (!conflict) return { isValid: true, message: "" };

    // Check if the same room is already selected for another row in this conflict
    const conflictRows = conflict.conflicting_data;

    for (let i = 0; i < conflictRows.length; i++) {
      if (i !== rowIndex) {
        const otherKey = `${conflictId}_${i}`;
        const otherSelectedRoom = resolutions[otherKey];

        if (otherSelectedRoom === selectedRoom) {
          const otherRowData = conflictRows[i];
          return {
            isValid: false,
            message: `Room ${selectedRoom} is already selected for Item no ${otherRowData.item_no} | Row ${otherRowData.row_number}. Please choose a different room to avoid creating another conflict.`,
          };
        }
      }
    }

    return { isValid: true, message: "" };
  };

  const handleOK = (conflictId, rowIndex) => {
    const key = `${conflictId}_${rowIndex}`;
    const selectedRoom = resolutions[key];

    if (selectedRoom) {
      // Validate the room selection
      const validation = validateRoomSelection(conflictId, rowIndex, selectedRoom);

      if (!validation.isValid) {
        alert(validation.message);
        return;
      }

      // Mark this resolution as confirmed
      setResolutions((prev) => ({
        ...prev,
        [`${key}_confirmed`]: true,
      }));
    }
  };

  const handleClear = (conflictId, rowIndex) => {
    const key = `${conflictId}_${rowIndex}`;
    setResolutions((prev) => {
      const newResolutions = { ...prev };
      delete newResolutions[key];
      delete newResolutions[`${key}_confirmed`];
      return newResolutions;
    });
  };

  const handleSaveAllChanges = () => {
    // Prepare resolution data for the parent component
    const resolutionData = conflicts.map((conflict) => ({
      conflict,
      resolutions: conflict.conflicting_data.map((rowData, rowIndex) => {
        const key = `${conflict.conflict_id}_${rowIndex}`;
        const selectedRoom = resolutions[key];
        const isConfirmed = resolutions[`${key}_confirmed`];

        // Check if this row is unresolvable due to insufficient rooms
        const availableForTimeSlot = availableRooms[conflict.day_time.toLowerCase()] || [];
        const conflictingRowsCount = conflict.conflicting_data.length;
        const availableRoomsCount = availableForTimeSlot.length;
        const totalAvailableRooms = availableRoomsCount + 1; // +1 for current conflict room
        const isInsufficientRooms = conflictingRowsCount > totalAvailableRooms;
        const canBeResolved = !isInsufficientRooms || rowIndex < totalAvailableRooms - 1;
        const mustRemainInOriginalRoom = isInsufficientRooms && !canBeResolved;

        return {
          row_number: rowData.row_number,
          new_room: isConfirmed ? selectedRoom : conflict.conflict_room, // Keep original if not confirmed
          changed: isConfirmed && selectedRoom !== conflict.conflict_room,
          needsExcelMarking: mustRemainInOriginalRoom, // Flag for Excel highlighting
        };
      }),
    }));

    onResolveConflicts(resolutionData);

    // Mark changes as processed to hide the save button
    setChangesProcessed(true);
  };

  const hasAnyResolutions = Object.keys(resolutions).some((key) => key.includes("_confirmed"));

  // Check if there are any rows that need Excel marking (unresolvable conflicts)
  const hasRowsNeedingExcelMarking = conflicts.some((conflict) => {
    const conflictingRowsCount = conflict.conflicting_data.length;
    const availableForTimeSlot = availableRooms[conflict.day_time.toLowerCase()] || [];
    const availableRoomsCount = availableForTimeSlot.length;
    const totalAvailableRooms = availableRoomsCount + 1; // +1 for current conflict room
    return conflictingRowsCount > totalAvailableRooms;
  });

  // Show save button if there are resolutions OR rows needing Excel marking (and changes haven't been processed yet)
  const shouldShowSaveButton =
    !changesProcessed && (hasAnyResolutions || hasRowsNeedingExcelMarking);

  if (!conflicts || conflicts.length === 0) {
    return <div className="text-center py-8 text-gray-500">No conflicts found</div>;
  }

  return (
    <div className="w-full space-y-6">
      {conflicts.map((conflict, conflictIndex) => {
        // Extract stable conflict number from conflict_id
        // Handle both old format ("CONFLICT_1") and new format ("Conflict #1")
        let conflictNumber;
        if (conflict.conflict_id) {
          if (conflict.conflict_id.startsWith("CONFLICT_")) {
            // Old format: "CONFLICT_1" -> "1"
            conflictNumber = conflict.conflict_id.replace("CONFLICT_", "");
          } else if (conflict.conflict_id.includes("Conflict #")) {
            // New format: "Conflict #1" -> "1"
            const match = conflict.conflict_id.match(/conflict\s*#(\d+)/i);
            conflictNumber = match ? match[1] : conflict.conflict_id;
          } else {
            // Fallback: use the ID as-is
            conflictNumber = conflict.conflict_id;
          }
        } else {
          // Fallback to index-based numbering
          conflictNumber = conflictIndex + 1;
        }

        // Determine styling based on parentheses presence
        const isParenthesesConflict = conflict.has_parentheses;
        const containerClass = isParenthesesConflict
          ? "border border-blue-200 rounded-lg p-4 bg-blue-50"
          : "border border-red-200 rounded-lg p-4 bg-red-50";
        const headerClass = isParenthesesConflict
          ? "mb-4 p-3 bg-blue-100 rounded-lg"
          : "mb-4 p-3 bg-red-100 rounded-lg";
        const titleClass = isParenthesesConflict
          ? "text-lg font-bold text-blue-800"
          : "text-lg font-bold text-red-800";
        const textClass = isParenthesesConflict ? "mt-2 text-blue-700" : "mt-2 text-red-700";
        const conflictIcon = isParenthesesConflict ? "⏰" : "🚨";

        return (
          <div key={conflict.conflict_id || conflictIndex} className={containerClass}>
            {/* Conflict Header */}
            <div className={headerClass}>
              <h3 className={titleClass}>
                {conflictIcon} Conflict #{conflictNumber}
              </h3>
              <div className={textClass}>
                <span className="font-semibold">Time Slot:</span> {conflict.day_time} |
                <span className="font-semibold ml-2">Room:</span> {conflict.conflict_room} |
                <span className="font-semibold ml-2">Excel Rows:</span> {conflict.conflict_rows} |
                <span className="font-semibold ml-2">
                  Item no ({conflict.conflicting_data.map((item) => item.item_no).join(", ")})
                </span>
              </div>
              <div className="mt-2 text-sm text-blue-700 bg-blue-50 p-2 rounded">
                <span className="font-semibold">Available Rooms for {conflict.day_time}:</span>{" "}
                {availableRooms[conflict.day_time.toLowerCase()]?.length > 0
                  ? availableRooms[conflict.day_time.toLowerCase()].join(", ")
                  : "None available"}
              </div>

              {/* Insufficient Rooms Warning */}
              {(() => {
                const conflictingRowsCount = conflict.conflicting_data.length;
                const availableRoomsCount =
                  availableRooms[conflict.day_time.toLowerCase()]?.length || 0;
                const totalAvailableRooms = availableRoomsCount + 1; // +1 for current conflict room
                const isInsufficientRooms = conflictingRowsCount > totalAvailableRooms;

                if (isInsufficientRooms) {
                  const shortfall = conflictingRowsCount - totalAvailableRooms;
                  return (
                    <div className="mt-2 p-3 bg-amber-100 border border-amber-300 rounded-lg">
                      <div className="flex items-start">
                        <span className="text-amber-600 text-lg mr-2 mt-0.5">⚠️</span>
                        <div className="flex-1">
                          <p className="font-semibold text-amber-800">
                            Insufficient Available Rooms
                          </p>
                          <p className="text-amber-700 text-sm mt-1">
                            This conflict has <strong>{conflictingRowsCount} rows</strong> but only{" "}
                            <strong>{totalAvailableRooms} rooms</strong> available (including
                            current room {conflict.conflict_room}).
                            <br />
                            <strong>
                              {shortfall} row{shortfall > 1 ? "s" : ""}
                            </strong>{" "}
                            cannot be automatically resolved and will be{" "}
                            <span className="font-semibold text-red-700">marked in yellow</span> in
                            the Excel export for manual intervention.
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
            </div>

            {/* Conflicting Rows Details */}
            <div className="space-y-3">
              <h4 className="font-semibold text-red-800 mb-2">Conflicting Assignments:</h4>

              {conflict.conflicting_data &&
                conflict.conflicting_data.map((rowData, rowIndex) => {
                  const key = `${conflict.conflict_id}_${rowIndex}`;
                  const selectedRoom = resolutions[key];
                  const isConfirmed = resolutions[`${key}_confirmed`];
                  const availableForTimeSlot =
                    availableRooms[conflict.day_time.toLowerCase()] || [];

                  // Check room availability constraints
                  const conflictingRowsCount = conflict.conflicting_data.length;
                  const availableRoomsCount = availableForTimeSlot.length;
                  const totalAvailableRooms = availableRoomsCount + 1; // +1 for current conflict room
                  const isInsufficientRooms = conflictingRowsCount > totalAvailableRooms;

                  // Determine if this specific row can be resolved
                  const canBeResolved = !isInsufficientRooms || rowIndex < totalAvailableRooms - 1; // Allow resolution for first N-1 rows
                  const mustRemainInOriginalRoom = isInsufficientRooms && !canBeResolved;

                  // Calculate how many rows need to be reassigned to resolve this conflict
                  const totalConflictingRows = conflict.conflicting_data.length;
                  const minRequiredReassignments = totalConflictingRows - 1; // N-1 rows need reassignment

                  // Count how many rows in this conflict have been confirmed for reassignment
                  const confirmedReassignments = conflict.conflicting_data.filter((_, idx) => {
                    const otherKey = `${conflict.conflict_id}_${idx}`;
                    return resolutions[`${otherKey}_confirmed`];
                  }).length;

                  // Conflict is truly resolved only when we have enough reassignments
                  const conflictResolved = confirmedReassignments >= minRequiredReassignments;

                  // This specific row is directly resolved
                  const directlyResolved = isConfirmed;

                  // This row is indirectly resolved (conflict resolved AND this row not directly reassigned)
                  const indirectlyResolved = conflictResolved && !directlyResolved;

                  return (
                    <div
                      key={rowIndex}
                      className={`bg-white border rounded p-3 ${
                        directlyResolved
                          ? "border-green-300 bg-green-50"
                          : indirectlyResolved
                          ? "border-green-300 bg-green-50"
                          : mustRemainInOriginalRoom
                          ? "border-amber-300 bg-amber-50"
                          : conflictResolved
                          ? "border-blue-300 bg-blue-50"
                          : confirmedReassignments > 0
                          ? "border-yellow-300 bg-yellow-50"
                          : "border-red-200"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span
                          className={`px-2 py-1 rounded text-sm font-semibold ${
                            directlyResolved
                              ? "bg-green-200 text-green-800"
                              : indirectlyResolved
                              ? "bg-green-200 text-green-800"
                              : mustRemainInOriginalRoom
                              ? "bg-amber-200 text-amber-800"
                              : conflictResolved
                              ? "bg-blue-200 text-blue-800"
                              : confirmedReassignments > 0
                              ? "bg-yellow-200 text-yellow-800"
                              : "bg-red-200 text-red-800"
                          }`}
                        >
                          <span className="text-green-600 font-bold">
                            Item no {rowData.item_no || "N/A"}
                          </span>{" "}
                          | Row {rowData.row_number}{" "}
                          {directlyResolved
                            ? "✓ Resolved"
                            : indirectlyResolved
                            ? "✓ Resolved (conflict resolved because other rows were reassigned)"
                            : mustRemainInOriginalRoom
                            ? "⚠️ Cannot resolve - will be marked in Excel"
                            : conflictResolved
                            ? `✓ Conflict resolved (${confirmedReassignments}/${minRequiredReassignments} reassignments complete)`
                            : `⚠️ Pending (need ${
                                minRequiredReassignments - confirmedReassignments
                              } more reassignment${
                                minRequiredReassignments - confirmedReassignments !== 1 ? "s" : ""
                              })`}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm mb-4">
                        <div>
                          <span className="font-medium text-gray-700">Unit:</span>
                          <div className="text-gray-900">{rowData.unit_name || "N/A"}</div>
                        </div>

                        <div>
                          <span className="font-medium text-gray-700">Tutor:</span>
                          <div className="text-gray-900">{rowData.tutor_name || "N/A"}</div>
                        </div>

                        <div>
                          <span className="font-medium text-gray-700">Students:</span>
                          <div className="text-gray-900">{rowData.students_coming || "N/A"}</div>
                        </div>

                        <div>
                          <span className="font-medium text-gray-700">Group:</span>
                          <div className="text-gray-900">{rowData.group || "N/A"}</div>
                        </div>

                        <div>
                          <span className="font-medium text-gray-700">Cohort:</span>
                          <div className="text-gray-900">
                            {rowData.cohort || rowData.intake || "N/A"}
                          </div>
                        </div>
                      </div>

                      {/* Room Resolution Controls */}
                      <div className="border-t pt-3">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                          <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Reassign to Room:
                            </label>
                            {(() => {
                              const validation = validateRoomSelection(
                                conflict.conflict_id,
                                rowIndex,
                                selectedRoom
                              );
                              return (
                                <>
                                  <select
                                    value={selectedRoom || ""}
                                    onChange={(e) =>
                                      handleRoomChange(
                                        conflict.conflict_id,
                                        rowIndex,
                                        e.target.value
                                      )
                                    }
                                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                                      !validation.isValid
                                        ? "border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50"
                                        : mustRemainInOriginalRoom
                                        ? "border-amber-300 bg-amber-50 cursor-not-allowed"
                                        : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                                    }`}
                                    disabled={directlyResolved || mustRemainInOriginalRoom}
                                  >
                                    <option value="">
                                      {mustRemainInOriginalRoom
                                        ? "Cannot reassign - insufficient rooms available"
                                        : indirectlyResolved
                                        ? "Optionally reassign to different room..."
                                        : "Select available room..."}
                                    </option>
                                    {!mustRemainInOriginalRoom &&
                                      availableForTimeSlot.map((room) => {
                                        // Check if this room is already selected by another row in this conflict
                                        const isDuplicateSelection = conflict.conflicting_data.some(
                                          (_, idx) => {
                                            if (idx !== rowIndex) {
                                              const otherKey = `${conflict.conflict_id}_${idx}`;
                                              return resolutions[otherKey] === room;
                                            }
                                            return false;
                                          }
                                        );

                                        return (
                                          <option
                                            key={room}
                                            value={room}
                                            style={
                                              isDuplicateSelection
                                                ? { color: "#ef4444", fontWeight: "bold" }
                                                : {}
                                            }
                                          >
                                            {room}
                                            {isDuplicateSelection
                                              ? " (⚠️ Already selected in this conflict)"
                                              : ""}
                                          </option>
                                        );
                                      })}
                                  </select>
                                  {!validation.isValid && (
                                    <div className="mt-1 text-sm text-red-600 bg-red-50 p-2 rounded border border-red-200">
                                      <span className="font-medium">⚠️ Conflict Warning:</span>{" "}
                                      {validation.message}
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>

                          <div className="flex gap-2">
                            {(() => {
                              const validation = validateRoomSelection(
                                conflictIndex,
                                rowIndex,
                                selectedRoom
                              );
                              return (
                                <button
                                  onClick={() => handleOK(conflict.conflict_id, rowIndex)}
                                  disabled={
                                    !selectedRoom ||
                                    directlyResolved ||
                                    !validation.isValid ||
                                    mustRemainInOriginalRoom
                                  }
                                  className={`px-4 py-2 text-white rounded-md font-medium min-w-[60px] ${
                                    !validation.isValid
                                      ? "bg-red-400 cursor-not-allowed"
                                      : mustRemainInOriginalRoom
                                      ? "bg-amber-400 cursor-not-allowed"
                                      : !selectedRoom || directlyResolved
                                      ? "bg-gray-300 cursor-not-allowed"
                                      : "bg-green-600 hover:bg-green-700"
                                  }`}
                                  title={
                                    !validation.isValid
                                      ? "Cannot confirm: Room already selected for another item in this conflict"
                                      : mustRemainInOriginalRoom
                                      ? "Cannot resolve: Insufficient available rooms for this time slot"
                                      : ""
                                  }
                                >
                                  OK
                                </button>
                              );
                            })()}
                            <button
                              onClick={() => handleClear(conflict.conflict_id, rowIndex)}
                              disabled={directlyResolved}
                              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium min-w-[60px]"
                            >
                              Clear
                            </button>
                          </div>
                        </div>

                        {directlyResolved && (
                          <div className="mt-2 text-sm text-green-700 bg-green-100 p-2 rounded">
                            ✓ Row {rowData.row_number} will be moved to room{" "}
                            <strong>{selectedRoom}</strong>
                          </div>
                        )}

                        {indirectlyResolved && !directlyResolved && (
                          <div className="mt-2 text-sm text-green-700 bg-green-100 p-2 rounded">
                            ✓ Conflict resolved! Row {rowData.row_number} will keep room{" "}
                            <strong>{conflict.conflict_room}</strong>
                          </div>
                        )}

                        {conflictResolved && !directlyResolved && !indirectlyResolved && (
                          <div className="mt-2 text-sm text-blue-700 bg-blue-100 p-2 rounded">
                            ℹ️ Conflict resolved by {confirmedReassignments} reassignment
                            {confirmedReassignments !== 1 ? "s" : ""}. Row {rowData.row_number} will
                            keep room <strong>{conflict.conflict_room}</strong>
                            (or optionally reassign above)
                          </div>
                        )}

                        {!conflictResolved &&
                          !directlyResolved &&
                          !mustRemainInOriginalRoom &&
                          confirmedReassignments > 0 && (
                            <div className="mt-2 text-sm text-amber-700 bg-amber-100 p-2 rounded">
                              ⏳ Partial progress: {confirmedReassignments}/
                              {minRequiredReassignments} required reassignments complete. Need{" "}
                              {minRequiredReassignments - confirmedReassignments} more to resolve
                              this conflict.
                            </div>
                          )}

                        {mustRemainInOriginalRoom && (
                          <div className="mt-2 text-sm text-amber-700 bg-amber-100 p-2 rounded border border-amber-200">
                            ⚠️ Row {rowData.row_number} cannot be reassigned due to insufficient
                            available rooms. Will remain in room{" "}
                            <strong>{conflict.conflict_room}</strong> and be{" "}
                            <span className="font-semibold">highlighted in yellow</span> in Excel
                            export for manual resolution.
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        );
      })}

      {/* Save All Changes Button */}
      {shouldShowSaveButton && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex flex-col sm:flex-row items-center justify-between">
            <div className="mb-3 sm:mb-0">
              <h3 className="font-semibold text-blue-800">💾 Ready to Apply Changes</h3>
              <p className="text-blue-700 text-sm">
                {hasAnyResolutions && hasRowsNeedingExcelMarking
                  ? 'You have confirmed resolutions and rows needing Excel marking. Click "Save All Changes" to apply all changes and update the web table and Excel export.'
                  : hasAnyResolutions
                  ? 'You have confirmed resolutions for some conflicts. Click "Save All Changes" to apply all changes and update the web table and Excel export.'
                  : 'Some rows cannot be resolved and will be marked in Excel. Click "Save All Changes" to process these changes and update the data tables.'}
              </p>
            </div>
            <button
              onClick={handleSaveAllChanges}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
            >
              Save All Changes
            </button>
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <h3 className="font-semibold text-yellow-800 mb-2">📊 Conflict Summary</h3>
        <p className="text-yellow-700">
          Found <strong>{conflicts.length}</strong> room allocation conflict
          {conflicts.length !== 1 ? "s" : ""}. Use the dropdowns above to reassign conflicting rows
          to available rooms. The available rooms shown exclude those already allocated for the same
          time slot.
        </p>
      </div>
    </div>
  );
};

export default ConflictTable;
